// Schemas for routes-audits.ts (browser-safe zod). PRD §3 WA-09, §4, §7, §17 (P1 advisor audits).
//
// Vocabulary
// - Advisor = a workspace Membership (role advisor or above) that handles conversations as a person.
// - Rubric  = versioned, per-workspace set of dimensions (weights, Spanish description, scoring guidance).
//             Stored in Workspace.settings.advisorAudits; every change bumps `version`.
// - Audit   = one AdvisorAudit row: a transcript-backed evaluation of ONE conversation (or call transcript) handled
//             by ONE advisor. Scores are 1–5 or null (not judged), every finding carries evidence (message ids +
//             quotes) and a confidence; weak evidence is flagged `uncertain`. Response speed is computed in code.
//             Corrections by a manager are stored separately; the original evaluation is never overwritten.
import { z } from 'zod';
import { AppointmentStatusSchema, ConversationModeSchema, HandoffStatusSchema, MessageAuthorSchema, RoleSchema } from './enums.js';
import { LlmStateSchema } from './api-agent-runtime.js';

const Id = z.string().min(1).max(64);
const Iso = z.string();
const Day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Usa el formato AAAA-MM-DD');

// ───────────── Rubric ─────────────

export const AUDIT_DIMENSIONS = ['response_speed', 'qualification_completeness', 'factual_accuracy', 'tone', 'objection_handling', 'next_step'] as const;
export const AuditDimensionSchema = z.enum(AUDIT_DIMENSIONS);
export type AuditDimension = z.infer<typeof AuditDimensionSchema>;

/** Dimensions scored by deterministic code, never by the model. */
export const DETERMINISTIC_DIMENSIONS: readonly AuditDimension[] = ['response_speed'];

export const RubricDimensionSchema = z.object({
  key: AuditDimensionSchema,
  label: z.string().trim().min(1).max(80),
  description: z.string().trim().min(1).max(600),
  /** How to score 1–5 (shown to reviewers and sent to the evaluator). */
  guidance: z.string().trim().min(1).max(1500),
  /** Relative weight for the overall score (0 = shown but not weighted). */
  weight: z.number().int().min(0).max(100),
  enabled: z.boolean(),
});
export type RubricDimension = z.infer<typeof RubricDimensionSchema>;

/** Minutes thresholds for response-speed scores 5/4/3/2 (above the last → 1). Ascending. */
export const ResponseSpeedThresholdsSchema = z
  .array(z.number().int().min(1).max(10_080))
  .length(4)
  .refine((a) => a.every((v, i) => i === 0 || v > a[i - 1]!), 'Los umbrales deben ser ascendentes');

const dimensionsSchema = z
  .array(RubricDimensionSchema)
  .length(AUDIT_DIMENSIONS.length)
  .refine((d) => new Set(d.map((x) => x.key)).size === AUDIT_DIMENSIONS.length, 'Cada dimensión debe aparecer una vez')
  .refine((d) => d.some((x) => x.enabled && x.weight > 0), 'Activa al menos una dimensión con peso mayor a cero');

export const AuditRubricSchema = z.object({
  version: z.number().int().min(1),
  dimensions: dimensionsSchema,
  responseSpeedThresholdsMinutes: ResponseSpeedThresholdsSchema,
  /** null = code default never edited. */
  updatedAt: Iso.nullable(),
  updatedByName: z.string().nullable(),
});
export type AuditRubric = z.infer<typeof AuditRubricSchema>;

export const UpdateAuditRubricSchema = z.object({
  dimensions: dimensionsSchema,
  responseSpeedThresholdsMinutes: ResponseSpeedThresholdsSchema,
  /** Optimistic concurrency: the version being edited. */
  expectedVersion: z.number().int().min(1),
});
export type UpdateAuditRubric = z.infer<typeof UpdateAuditRubricSchema>;

export const AuditSamplingSchema = z.object({
  /** Scheduled audits (worker) — off by default: they spend platform model tokens. */
  enabled: z.boolean(),
  /** Conversations sampled per advisor per calendar week (workspace timezone). */
  perAdvisorPerWeek: z.number().int().min(1).max(20),
  /** A conversation qualifies when the advisor sent at least this many messages in it. */
  minAdvisorMessages: z.number().int().min(1).max(50),
});
export type AuditSampling = z.infer<typeof AuditSamplingSchema>;

export const AuditConfigSchema = z.object({
  rubric: AuditRubricSchema,
  sampling: AuditSamplingSchema,
  llm: z.object({ state: LlmStateSchema, model: z.string().nullable(), reason: z.string().nullable() }),
  /** Plain-language privacy statement shown next to the configuration. */
  privacyNote: z.string(),
});
export type AuditConfig = z.infer<typeof AuditConfigSchema>;

export const DEFAULT_RESPONSE_SPEED_THRESHOLDS = [5, 15, 60, 240] as const;

export const DEFAULT_AUDIT_RUBRIC: readonly RubricDimension[] = [
  {
    key: 'response_speed',
    label: 'Rapidez de respuesta',
    description: 'Qué tan rápido responde el asesor a los mensajes del prospecto una vez que la conversación es suya.',
    guidance:
      'Se calcula automáticamente (no lo decide el modelo): mediana del tiempo entre un mensaje del prospecto y la siguiente respuesta del asesor. 5 = dentro del primer umbral; 1 = por encima del último. Sin pares medibles queda sin calificar.',
    weight: 15,
    enabled: true,
  },
  {
    key: 'qualification_completeness',
    label: 'Calificación completa',
    description: 'El asesor confirma o completa los datos de calificación (presupuesto, plazo, forma de pago, tipo de unidad, uso) sin repetir lo que ya se sabe.',
    guidance:
      '5 = confirma los datos clave faltantes de forma natural; 3 = obtiene algunos datos pero omite otros relevantes; 1 = no intenta calificar o repite preguntas ya respondidas. Sin oportunidad de calificar → sin calificar.',
    weight: 20,
    enabled: true,
  },
  {
    key: 'factual_accuracy',
    label: 'Exactitud de la información',
    description: 'Precios, disponibilidad, ubicación y condiciones coinciden con el inventario y los documentos publicados.',
    guidance:
      '5 = todo dato verificable coincide con la fuente; 3 = imprecisiones menores; 1 = información contradictoria con el inventario o documentos. Cada hallazgo debe citar la unidad, el precio o el documento; si no hay fuente, se marca como incierto.',
    weight: 25,
    enabled: true,
  },
  {
    key: 'tone',
    label: 'Tono y cortesía',
    description: 'Trato profesional, cordial y claro, adecuado para WhatsApp.',
    guidance: '5 = cordial, claro y personalizado; 3 = correcto pero impersonal o con errores menores; 1 = descortés, confuso o presionante.',
    weight: 10,
    enabled: true,
  },
  {
    key: 'objection_handling',
    label: 'Manejo de objeciones',
    description: 'Atiende dudas y objeciones (precio, ubicación, tiempos de entrega, financiamiento) con información y empatía.',
    guidance: '5 = reconoce la objeción y responde con datos o alternativas; 3 = responde parcialmente; 1 = ignora o descalifica la objeción. Sin objeciones → sin calificar.',
    weight: 15,
    enabled: true,
  },
  {
    key: 'next_step',
    label: 'Siguiente paso',
    description: 'Cierra con un siguiente paso concreto: cita, llamada, envío de información o fecha de seguimiento.',
    guidance: '5 = propone y acuerda un siguiente paso con fecha; 3 = sugiere un paso sin concretarlo; 1 = la conversación queda sin siguiente paso.',
    weight: 15,
    enabled: true,
  },
];

export const DEFAULT_AUDIT_SAMPLING: AuditSampling = { enabled: false, perAdvisorPerWeek: 2, minAdvisorMessages: 3 };

export const AUDIT_PRIVACY_NOTE =
  'Las auditorías usan solo las conversaciones, el inventario y los documentos publicados de este espacio de trabajo. No se usan conversaciones de clientes para evaluar ni entrenar a otros clientes.';

// ───────────── Audits ─────────────

export const ADVISOR_AUDIT_STATUSES = ['pending', 'completed', 'insufficient_evidence', 'failed', 'reviewed'] as const;
export const AdvisorAuditStatusSchema = z.enum(ADVISOR_AUDIT_STATUSES);
export type AdvisorAuditStatus = z.infer<typeof AdvisorAuditStatusSchema>;
export const ADVISOR_AUDIT_STATUS_LABELS: Record<AdvisorAuditStatus, string> = {
  pending: 'En proceso',
  completed: 'Completada',
  insufficient_evidence: 'Evidencia insuficiente',
  failed: 'No se pudo evaluar',
  reviewed: 'Revisada',
};

export const AUDIT_FINDING_SEVERITIES = ['positive', 'minor', 'major'] as const;
export const AuditFindingSeveritySchema = z.enum(AUDIT_FINDING_SEVERITIES);
export type AuditFindingSeverity = z.infer<typeof AuditFindingSeveritySchema>;
export const AUDIT_FINDING_SEVERITY_LABELS: Record<AuditFindingSeverity, string> = { positive: 'Fortaleza', minor: 'Mejora menor', major: 'Problema importante' };

export const AUDIT_SOURCE_TYPES = ['unit', 'document', 'project'] as const;
export const AuditSourceTypeSchema = z.enum(AUDIT_SOURCE_TYPES);

export const AuditEvidenceSchema = z.object({
  messageId: Id,
  quote: z.string(),
  /** false when the quote does not appear verbatim in the cited message (the finding is then uncertain). */
  quoteVerified: z.boolean(),
});
export type AuditEvidence = z.infer<typeof AuditEvidenceSchema>;

export const AuditSourceRefSchema = z.object({ type: AuditSourceTypeSchema, id: Id, label: z.string() });
export type AuditSourceRef = z.infer<typeof AuditSourceRefSchema>;

export const AuditFindingSchema = z.object({
  id: Id,
  dimension: AuditDimensionSchema,
  severity: AuditFindingSeveritySchema,
  text: z.string(),
  evidence: z.array(AuditEvidenceSchema),
  /** Authoritative sources (inventory unit/price, published document, project facts). Required for accuracy issues. */
  sources: z.array(AuditSourceRefSchema),
  confidence: z.number().min(0).max(1),
  uncertain: z.boolean(),
  /** Why the finding is uncertain (Spanish), when it is. */
  uncertainReason: z.string().nullable(),
  origin: z.enum(['model', 'deterministic']),
});
export type AuditFinding = z.infer<typeof AuditFindingSchema>;

export const AuditDimensionScoreSchema = z.object({
  dimension: AuditDimensionSchema,
  label: z.string(),
  weight: z.number(),
  /** 1–5; null = not judged (no opportunity or no evidence). Never defaulted. */
  score: z.number().int().min(1).max(5).nullable(),
  confidence: z.number().min(0).max(1).nullable(),
  uncertain: z.boolean(),
  rationale: z.string().nullable(),
  evidenceMessageIds: z.array(Id),
  origin: z.enum(['model', 'deterministic']),
});
export type AuditDimensionScore = z.infer<typeof AuditDimensionScoreSchema>;

export const AuditMetricsSchema = z.object({
  advisorMessages: z.number().int(),
  contactMessages: z.number().int(),
  /** Median seconds between a contact message and the advisor's next reply; null = no measurable pair. */
  medianReplySeconds: z.number().nullable(),
  maxReplySeconds: z.number().nullable(),
  replyPairs: z.number().int(),
  /** Findings dropped because their evidence pointed outside the audited transcript. */
  rejectedFindings: z.number().int(),
});
export type AuditMetrics = z.infer<typeof AuditMetricsSchema>;

export const AuditTranscriptEntrySchema = z.object({
  id: Id,
  author: MessageAuthorSchema,
  authorName: z.string().nullable(),
  body: z.string().nullable(),
  createdAt: Iso.nullable(),
  /** Cited by at least one finding or score. */
  cited: z.boolean(),
});
export type AuditTranscriptEntry = z.infer<typeof AuditTranscriptEntrySchema>;

export const AuditReviewSchema = z.object({
  reviewedByName: z.string().nullable(),
  reviewedAt: Iso,
  note: z.string(),
  /** Scores changed by the reviewer (dimension → new score or null). The original stays in `scores`. */
  correctedScores: z.array(z.object({ dimension: AuditDimensionSchema, score: z.number().int().min(1).max(5).nullable() })),
  /** Finding decisions by the reviewer; the original finding stays in `findings`. */
  findingDecisions: z.array(z.object({ findingId: Id, decision: z.enum(['confirmed', 'rejected', 'edited']), text: z.string().nullable() })),
  /** Status before the review (completed / insufficient_evidence / failed). */
  previousStatus: AdvisorAuditStatusSchema,
});
export type AuditReview = z.infer<typeof AuditReviewSchema>;

export const AdvisorAuditSummarySchema = z.object({
  id: Id,
  status: AdvisorAuditStatusSchema,
  conversationId: Id.nullable(),
  callId: Id.nullable(),
  membershipId: Id.nullable(),
  advisorName: z.string().nullable(),
  contactName: z.string().nullable(),
  projectName: z.string().nullable(),
  rubricVersion: z.number().int(),
  /** Weighted average of the effective (corrected when reviewed) scores, 1–5; null when nothing was scored. */
  overallScore: z.number().nullable(),
  uncertain: z.boolean(),
  trigger: z.enum(['manual', 'scheduled']),
  simulated: z.boolean(),
  acknowledgedAt: Iso.nullable(),
  reviewedAt: Iso.nullable(),
  createdAt: Iso,
});
export type AdvisorAuditSummary = z.infer<typeof AdvisorAuditSummarySchema>;

export const AdvisorAuditDetailSchema = AdvisorAuditSummarySchema.extend({
  summary: z.string().nullable(),
  /** Why the audit failed / has insufficient evidence / is pending (Spanish). */
  statusDetail: z.string().nullable(),
  scores: z.array(AuditDimensionScoreSchema),
  /** Scores after the reviewer's corrections (equal to `scores` when not corrected). */
  effectiveScores: z.array(AuditDimensionScoreSchema),
  findings: z.array(AuditFindingSchema),
  metrics: AuditMetricsSchema.nullable(),
  transcript: z.array(AuditTranscriptEntrySchema),
  /** Transcript entries withheld because they belong to projects the caller cannot access. */
  withheldEntries: z.number().int(),
  model: z.object({ provider: z.string(), model: z.string(), simulated: z.boolean() }).nullable(),
  review: AuditReviewSchema.nullable(),
  acknowledgedByName: z.string().nullable(),
  canReview: z.boolean(),
  canAcknowledge: z.boolean(),
});
export type AdvisorAuditDetail = z.infer<typeof AdvisorAuditDetailSchema>;

export const AuditListQuerySchema = z.object({
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  membershipId: Id.optional(),
  status: AdvisorAuditStatusSchema.optional(),
  /** "true" → only uncertain audits; "false" → only certain ones. */
  uncertain: z.enum(['true', 'false']).optional(),
  conversationId: Id.optional(),
});
export type AuditListQuery = z.infer<typeof AuditListQuerySchema>;

export const AuditListSchema = z.object({ items: z.array(AdvisorAuditSummarySchema), nextCursor: z.string().nullable() });

export const CreateAdvisorAuditSchema = z
  .object({
    conversationId: Id.optional(),
    /** A call with a stored transcript; requires `membershipId` (the call transcript cannot identify the advisor). */
    callId: Id.optional(),
    /** Advisor to evaluate; default = the member who wrote most human messages in the conversation. */
    membershipId: Id.optional(),
  })
  .refine((b) => !!b.conversationId !== !!b.callId, { message: 'Indica una conversación o una llamada', path: ['conversationId'] })
  .refine((b) => !b.callId || !!b.membershipId, { message: 'Para una llamada indica el asesor', path: ['membershipId'] });
export type CreateAdvisorAudit = z.infer<typeof CreateAdvisorAuditSchema>;

export const ReviewAdvisorAuditSchema = z.object({
  note: z.string().trim().min(3).max(2000),
  correctedScores: z.array(z.object({ dimension: AuditDimensionSchema, score: z.number().int().min(1).max(5).nullable() })).max(AUDIT_DIMENSIONS.length).default([]),
  findingDecisions: z
    .array(z.object({ findingId: Id, decision: z.enum(['confirmed', 'rejected', 'edited']), text: z.string().trim().min(3).max(600).nullable().optional() }))
    .max(40)
    .default([]),
});
export type ReviewAdvisorAudit = z.infer<typeof ReviewAdvisorAuditSchema>;

export const AuditCandidatesQuerySchema = z.object({ membershipId: Id.optional(), days: z.coerce.number().int().min(1).max(90).default(14) });
export const AuditCandidateSchema = z.object({
  conversationId: Id,
  contactName: z.string().nullable(),
  projectName: z.string().nullable(),
  membershipId: Id,
  advisorName: z.string().nullable(),
  advisorMessages: z.number().int(),
  lastAdvisorMessageAt: Iso,
  lastAuditAt: Iso.nullable(),
});
export type AuditCandidate = z.infer<typeof AuditCandidateSchema>;

// ───────────── Advisor performance report ─────────────

export const AdvisorPerformanceQuerySchema = z.object({
  from: Day.optional(),
  to: Day.optional(),
  projectId: Id.optional(),
  membershipId: Id.optional(),
});
export type AdvisorPerformanceQuery = z.infer<typeof AdvisorPerformanceQuerySchema>;

/** Same shape as ReportMetric (usage reports): null = no data, never 0. */
export const AdvisorMetricSchema = z.object({
  value: z.number().nullable(),
  numerator: z.number().nullable(),
  denominator: z.number().nullable(),
  unit: z.enum(['count', 'ratio', 'seconds', 'score']),
});
export type AdvisorMetric = z.infer<typeof AdvisorMetricSchema>;

export const ADVISOR_METRICS = [
  'assignedConversations',
  'conversationsHandled',
  'messagesSent',
  'firstResponseMedian',
  'handoffsAssigned',
  'handoffAckMedian',
  'handoffResolutionMedian',
  'openHandoffs',
  'appointmentsBooked',
  'appointmentsAttended',
  'appointmentsNoShow',
  'internalNotes',
  'leadsNeedingAction',
  'auditScore',
] as const;
export type AdvisorMetricKey = (typeof ADVISOR_METRICS)[number];

export const AdvisorRowSchema = z.object({
  membershipId: Id,
  name: z.string(),
  role: RoleSchema,
  metrics: z.object(Object.fromEntries(ADVISOR_METRICS.map((k) => [k, AdvisorMetricSchema])) as Record<AdvisorMetricKey, typeof AdvisorMetricSchema>),
  leadsByStage: z.array(z.object({ stage: z.string(), count: z.number().int() })),
});
export type AdvisorRow = z.infer<typeof AdvisorRowSchema>;

export const AdvisorPerformanceSchema = z.object({
  range: z.object({ from: Day, to: Day, start: Iso, end: Iso, timezone: z.string() }),
  filters: z.object({ projectId: Id.nullable(), membershipId: Id.nullable() }),
  /** true when the caller is an advisor and only sees their own row. */
  selfOnly: z.boolean(),
  isDemo: z.boolean(),
  definitions: z.record(z.string(), z.object({ label: z.string(), definition: z.string() })),
  advisors: z.array(AdvisorRowSchema),
});
export type AdvisorPerformance = z.infer<typeof AdvisorPerformanceSchema>;

// ───────────── "Mi trabajo" (advisor view) ─────────────

export const MyWorkSchema = z.object({
  membershipId: Id,
  timezone: z.string(),
  conversations: z.array(
    z.object({
      id: Id,
      contactName: z.string().nullable(),
      projectName: z.string().nullable(),
      mode: ConversationModeSchema,
      lastMessageAt: Iso.nullable(),
      awaitingReply: z.boolean(),
      unreadCount: z.number().int(),
    }),
  ),
  handoffs: z.array(
    z.object({
      id: Id,
      conversationId: Id.nullable(),
      contactName: z.string().nullable(),
      projectName: z.string().nullable(),
      reason: z.string(),
      recommendedNextStep: z.string().nullable(),
      status: HandoffStatusSchema,
      createdAt: Iso,
      /** Plan B7: a missed-call task — the latest unanswered call linked to it, how many, and whether a voicemail was left. */
      missedCall: z.object({ callId: Id, voicemail: z.boolean(), calls: z.number().int() }).nullable().default(null),
    }),
  ),
  visits: z.array(
    z.object({
      id: Id,
      contactName: z.string().nullable(),
      projectName: z.string(),
      status: AppointmentStatusSchema,
      startAt: Iso,
      location: z.string().nullable(),
    }),
  ),
  leads: z.array(
    z.object({
      id: Id,
      contactId: Id,
      contactName: z.string().nullable(),
      projectName: z.string(),
      stage: z.string(),
      nextAction: z.string().nullable(),
      nextActionAt: Iso.nullable(),
      overdue: z.boolean(),
    }),
  ),
  auditsToAcknowledge: z.number().int(),
  truncated: z.object({ conversations: z.boolean(), handoffs: z.boolean(), visits: z.boolean(), leads: z.boolean() }),
});
export type MyWork = z.infer<typeof MyWorkSchema>;
